import { ResetGameCommand } from "ags/commands/reset-game-fatal-error-command";
import { AgsRecordBuilder } from "ags/integration/builders/records/abstract-ags-record-builder";
import { AgsFeatureTypes, AgsPickSchema, AgsWin, AgsWinTypes } from "ags/integration/schema/ags-spin-response-schema";
import { Gameplay } from "appworks/model/gameplay/gameplay";
import { Record } from "appworks/model/gameplay/records/record";
import { RecordBuilder } from "appworks/server/builders/record-builder";
import { PickMatchRecord } from "slotworks/model/gameplay/records/pick-match-record";
import { PickRequestPayload } from "slotworks/model/gameplay/requests/pick-request-payload";
import { isUndefined } from "util";
import { AgsSpinPublicStateSchema } from "../../schema/ags-spin-response-schema";

export class PickRecordBuilder extends AgsRecordBuilder implements RecordBuilder {
    public id: string = "AgsPickRecordBuilder";

    public filter(request: any, response: any): boolean {
        return request instanceof PickRequestPayload;
    }

    public build(gameplay: Gameplay, request: any, response: any): Record[] {
        const rootData = this.getRootAgsRecord(response);

        const platform = rootData.platform;
        if (platform.responsibleGamingEvent) {
            // SG requirement - fatal error
            ResetGameCommand();
            return [];
        }

        super.build(gameplay, request, response);

        const latestPick = this.getLatestAgsSpin(rootData.publicState) as AgsPickSchema;

        let spinComplete = latestPick.complete;
        if (this.hasPendingBonuses(rootData.publicState)) {
            spinComplete = false;
        }

        const record = this.buildRecord(latestPick, spinComplete);
        return [record];
    }

    public buildRecord(pick: AgsPickSchema, spinComplete: boolean): PickMatchRecord {
        const record = new PickMatchRecord();
        record.index = pick.index;
        record.value = pick.value;
        record.isPrize = pick.complete;
        record.lastChild = isUndefined(spinComplete) ? pick.complete : spinComplete;
        record.sumWins = pick.cashWon || 0;
        record.complete = pick.complete;

        return record;
    }

    protected hasPendingBonuses(rootState: AgsSpinPublicStateSchema): boolean {
        const freespinsNotStarted = this.freespinsNotStarted(rootState);
        const hasUnplayedHoldAndSpin = this.hasUnplayedHoldAndSpin(rootState);
        return freespinsNotStarted || hasUnplayedHoldAndSpin;
    }

    protected hasUnplayedHoldAndSpin(rootState: AgsSpinPublicStateSchema): boolean {
        const spin = rootState.result;

        let holdAndSpinTriggered = false;
        let holdAndSpinStarted = false;

        if (spin.wins.length > 0) {
            spin.wins.forEach((win: AgsWin) => {
                if (win.winType === AgsWinTypes.RANDOM && win.featureType === AgsFeatureTypes.PICK) {
                    holdAndSpinTriggered = true;
                }
            });
        }

        if (holdAndSpinTriggered) {
            for (const childFeature of spin.childFeature) {
                if (childFeature.type === AgsFeatureTypes.PICK) {
                    holdAndSpinStarted = true;
                }
            }
        }

        return holdAndSpinTriggered && !holdAndSpinStarted;
    }

    protected freespinsNotStarted(rootState: AgsSpinPublicStateSchema): boolean {
        const spin = rootState.result;

        let freespinsTriggered = false;
        let freespinsStarted = false;
        if (spin.wins.length > 0) {
            spin.wins.forEach((win: AgsWin) => {
                if (win.freeSpinsAmount > 0 || win.featureType === AgsFeatureTypes.FREESPINS_WIN) {
                    freespinsTriggered = true;
                }
            });
        }

        if (freespinsTriggered) {
            for (const childFeature of spin.childFeature) {
                if (childFeature.type === "freeSpin") {
                    freespinsStarted = true;
                }
            }
        }

        return freespinsTriggered && !freespinsStarted;
    }
}
